import React, { useMemo, useState } from 'react';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { Box, Button, Flex, HStack, Text } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../constants/cypressIds';
import { AgentPortalDuskIds } from '../../../../constants/duskIds';
import { getValidationObject, validationTypes } from '../../../../constants/regexPatterns';
import { createErrorToast } from '../../../../redux/modules/app';
import { useCreateAgency, useInsuranceRequestAgencies } from '../../../../services/insurance-request-service';
import { getInfiniteListData } from '../../../../services/util';
import colors from '../../../../styles/theme/foundation/colors';
import FormInput from '../../../form/FormInput';
import { inputTypes } from '../../../form/FormInput/util';
import InputLabel from '../../../form/InputLabel';
import ReactQueryInfiniteTable from '../../../ReactQueryInfiniteTable';
import LoadingSpinner from '../../../Spinner';

import { getAgencyColumns } from './columns';
import { parseAddress } from './util';

const AgenciesTable = ({ handleRowClick, selectedAgencyId }) => {
  const dispatch = useDispatch();
  const { insuranceRequestId } = useParams();

  const COLUMNS = useMemo(() => getAgencyColumns(insuranceRequestId), [insuranceRequestId]);

  const useFormMethods = useForm();
  const {
    getValues,
    formState: { errors },
    trigger,
    reset
  } = useFormMethods;

  const rqAgencies = useInsuranceRequestAgencies({ insuranceRequestId });
  const { mutate: createAgency } = useCreateAgency({ insuranceRequestId });

  const rqAgenciesInfiniteListData = useMemo(() => {
    if (rqAgencies.isLoading) {
      return [];
    }

    return getInfiniteListData(rqAgencies.data);
  }, [rqAgencies]);

  const [displayCreateAgency, setDisplayCreateAgency] = useState(false);
  const [address, setAddress] = useState(null);

  const pulse = keyframes`
    0% {transform: scale(1,1);}
    50% {opacity: 1;}
    100% {transform: scale(1.18); opacity: 1;}
  `;

  if (rqAgencies.isLoading) {
    return (
      <Box>
        <Text>Loading agencies...</Text>
        <LoadingSpinner minHeight="100px" />
      </Box>
    );
  }

  const onSubmit = async () => {
    if (!address) {
      dispatch(createErrorToast({
        title: 'Please select an address before saving',
        duration: 5000
      }));

      return false;
    }

    const isValid = await trigger();

    if (isValid) {
      const values = getValues();
      const geocodeResult = await geocodeByPlaceId(address.value.place_id);

      const parsedAddress = parseAddress(geocodeResult?.length ? geocodeResult[0]?.address_components ?? [] : []);

      const data = {
        name: values.company,
        address: parsedAddress.companyAddress,
        city: parsedAddress.city,
        state: parsedAddress.state,
        zip: parsedAddress.postalCode,
        country: parsedAddress.country,
        googlePlaceId: address.value.place_id,
        license: values.license
      };

      createAgency(data);

      setDisplayCreateAgency(false);
      reset();
      setAddress(null);
    } else {
      // eslint-disable-next-line no-console
      console.error('Form has an error', { errors });
    }
  };

  return (
    <Box maxW="1000px" mx="auto">
      <Text fontSize="bodyXl" mb={4} mt={8}>
        Which company/agency are you submitting this certificate on behalf of?  Please select one.
      </Text>

      {!!rqAgenciesInfiniteListData.length && (
        <ReactQueryInfiniteTable
          height="150px"
          noDataMessage="No Agencies Created.  Please create a new company/agency."
          reactQueryResponse={rqAgencies}
          rowKey="agencyId"
          selectable={{
            selectedItemId: selectedAgencyId,
            handleItemSelect: (e, rowId) => {
              if (rowId !== selectedAgencyId) {
                handleRowClick(e, rowId);
              }
            }
          }}
          tableColumns={COLUMNS}
        />
      )}

      {displayCreateAgency && (
        <Flex
          alignItems="center"
          flexFlow="row wrap"
          justifyContent="center"
          mb="20px"
          minH="100px"
          mt={4}
        >
          <HStack>
            <FormInput
              cypressId={CypressIds.certAgentDetailsLicenseInput}
              data-analytics-id="agent-portal-license-input"
              duskSelector={AgentPortalDuskIds.verifyLicenseInput}
              inputName="license"
              labelName="License/Broker #"
              maxW="400px"
              mx={4}
              placeholder="#########"
              toolTip={'Don\'t have one?  Please enter N/A'}
              type={inputTypes.standard}
              useFormMethods={useFormMethods}
              validationRules={{ ...getValidationObject(validationTypes.alphaNumericAndCharacters), required: 'Please provide your license number.' }}
            />

            <FormInput
              cypressId={CypressIds.certAgentDetailsCompanyNameInput}
              data-analytics-id="agent-portal-company-input"
              duskSelector={AgentPortalDuskIds.verifyCompanyInput}
              height="100px"
              inputName="company"
              labelName="Company/Agency Name"
              maxW="400px"
              mx={4}
              placeholder="ABC Agency"
              type={inputTypes.standard}
              useFormMethods={useFormMethods}
              validationRules={{ ...getValidationObject(validationTypes.alphaNumericAndCharacters), required: 'Please provide your company/agency name.' }}
            />

            <Box height="100px" minW="300px">
              <InputLabel labelName="Company/Agency Address" width="auto" />
              <Box
                data-cy={CypressIds.certAgentDetailsAddressInput}
                duskId={AgentPortalDuskIds.verifyAddressInput}
              >
                <GooglePlacesAutocomplete
                  apiKey={GOOGLE_PLACES_API_KEY}
                  selectProps={{
                    address,
                    onChange: setAddress,
                    placeholder: 'Type address here...',
                    styles: {
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        color: colors.greyScale['900'],
                        background: state.isFocused ? 'white' : colors.greyScale['100'],
                        borderRadius: '10px',
                        border: state.isFocused ? '0px' : '1px',
                        fontWeight: '400',
                        fontSize: '15px', // sm
                        letterSpacing: '-0.02em',
                        lineHeight: '20px',
                        textAlign: 'left'
                      }),
                      input: provided => ({
                        ...provided,
                        color: colors.greyScale['900']
                      }),
                      option: provided => ({
                        ...provided,
                        color: colors.greyScale['900']
                      }),
                      singleValue: provided => ({
                        ...provided,
                        color: colors.greyScale['900']
                      }),
                      placeholder: provided => ({
                        ...provided,
                        color: colors.greyScale['500']
                      })
                    }
                  }}
                />
              </Box>
            </Box>
          </HStack>
        </Flex>
      )}

      <Flex
        alignItems="center"
        flexFlow="row wrap"
        justifyContent="center"
        mb="20px"
        minH="100px"
      >
        <HStack>
          <Button
            animation={!rqAgenciesInfiniteListData.length && !displayCreateAgency ? `${pulse} 1.5s ease infinite` : undefined}
            data-cy={CypressIds.certAgentDetailsCreateAgencyButton}
            variant={displayCreateAgency || !!rqAgenciesInfiniteListData.length ? 'secondary' : 'primary'}
            onClick={() => setDisplayCreateAgency(!displayCreateAgency)}
          >
            {displayCreateAgency ? 'Cancel' : 'Create New Company/Agency'}
          </Button>

          {displayCreateAgency && (
            <Button
              data-cy={CypressIds.certAgentDetailsCreateAgencySubmitButton}
              onClick={onSubmit}
            >
              Create
            </Button>
          )}
        </HStack>
      </Flex>
    </Box>
  );
};

AgenciesTable.propTypes = {
  handleRowClick: PropTypes.func.isRequired,
  selectedAgencyId: PropTypes.number
};

export default AgenciesTable;
